import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  LinearProgress,
  Chip,
  TextField,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Build as BuildIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Layout from '../../components/Layout';
import { useMst } from '../../model/Root';
import {
  diagnoseStripe,
  autoFixStripeIssues,
  updateUserResetDate,
  diagnoseSingleUser,
  searchUsersWithSubscriptionDetails,
  UserWithSubscriptionDetails,
} from '../../services/subscriptionDiagnosticsApi';

interface StripeResetIssue {
  userId: number;
  userEmail: string;
  currentResetDate: string | null;
  isActive: boolean;
  customerId: string;
  paymentType?: number; // Add paymentType for proper subscription type detection
  subscriptionStartDate?: string | null; // Add subscription anniversary date
  issueType: string; // Add issue type for better display
}

interface SelectedUserDetails {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  nextUsageResetDate: string | null;
  customerId: string | null;
  subscriber: boolean;
  paymentType?: number; // Add paymentType for proper subscription type detection
  subscriptionStartDate?: string | null; // Add subscription start date
}

const SubscriptionManagementScreen: React.FC = observer(() => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<StripeResetIssue[]>([]);
  const [workingUsers, setWorkingUsers] = useState<StripeResetIssue[]>([]);
  const [hasScanned, setHasScanned] = useState(false);
  const [stats, setStats] = useState({
    totalActiveSubscribers: 0,
    usersWithBrokenResetDate: 0,
    usersWithValidResetDate: 0,
  });

  // Manual user management state
  const [selectedUser, setSelectedUser] = useState<SelectedUserDetails | null>(null);
  const [isEditingResetDate, setIsEditingResetDate] = useState(false);
  const [newResetDate, setNewResetDate] = useState<Date | null>(null);
  const [userSearchOptions, setUserSearchOptions] = useState<UserWithSubscriptionDetails[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [fixingUserId, setFixingUserId] = useState<number | null>(null);

  const {
    errorStore: { setError, setSuccess },
  } = useMst();

  // Search users with subscription details
  const searchUsers = async (query: string) => {
    if (query.length < 2) {
      setUserSearchOptions([]);
      return;
    }

    setUserSearchLoading(true);
    try {
      const response = await searchUsersWithSubscriptionDetails({ query, limit: 50 });
      setUserSearchOptions(response.users);
    } catch (error) {
      console.error('Failed to search users:', error);
      setUserSearchOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Scan for active Stripe subscribers with null nextUsageResetDate
  const handleScanUsers = async () => {
    setLoading(true);
    try {
      const response = await diagnoseStripe({
        fix: false,
        limit: 1000, // Get all affected users
      });

      // Check if response has the expected structure
      if (!response || !response.issues || !Array.isArray(response.issues)) {
        console.error('Unexpected API response structure:', response);
        setError('API returned unexpected response format. Please check backend implementation.');
        return;
      }

      // Filter to only reset date issues (ignore other problem types)
      const resetDateIssues = response.issues.filter(
        (issue) =>
          issue.issueType === 'missing_reset_date' ||
          issue.issueType === 'invalid_reset_date' ||
          issue.issueType === 'outdated_reset_date' ||
          issue.issueType === 'misaligned_anniversary_date'
      );

      // Transform to simplified format
      const simpleIssues: StripeResetIssue[] = resetDateIssues.map((issue) => {
        // FIX: Issues array doesn't contain subscriptionStartDate, but allUsers array does
        // Cross-reference with allUsers array to get the subscription start date
        const matchingUser = response.allUsers?.find((user) => user.userId === issue.userId);
        const startDate = matchingUser?.subscriptionStartDate || null;

        return {
          userId: issue.userId,
          userEmail: issue.userEmail,
          currentResetDate: issue.databaseData?.nextUsageResetDate || null,
          isActive: true, // Pre-filtered to only active users
          customerId: issue.stripeData?.customerId || '',
          paymentType: issue.databaseData?.paymentType,
          subscriptionStartDate: startDate, // ← NOW GETS CORRECT DATA!
          issueType: issue.issueType,
        };
      });

      // Process working users (only if allUsers is available)
      let workingUsersList: StripeResetIssue[] = [];
      if (response.allUsers && Array.isArray(response.allUsers)) {
        workingUsersList = response.allUsers
          .filter((user) => !user.hasIssues) // All users now have proper reset dates, just filter by hasIssues
          .map((user) => {
            // FIX: For working users, subscription start date is directly available
            const startDate = user.subscriptionStartDate || null;

            return {
              userId: user.userId,
              userEmail: user.userEmail,
              currentResetDate: user.nextUsageResetDate, // All users now have this
              isActive: true,
              customerId: user.customerId || '',
              paymentType: user.paymentType,
              subscriptionStartDate: startDate,
              issueType: 'none', // Working users have no issues
            };
          });
      }

      setIssues(simpleIssues);
      setWorkingUsers(workingUsersList);
      // Calculate broken reset dates - ALL issues from API are considered broken
      const brokenResetDates = simpleIssues; // All issues returned by API are broken

      setStats({
        totalActiveSubscribers: response.summary?.databaseUsersChecked || 0,
        usersWithBrokenResetDate: brokenResetDates.length,
        usersWithValidResetDate: workingUsersList.length,
      });

      setHasScanned(true);
      setSuccess(
        `${t('subscription_management.scan_complete')}: ${simpleIssues.length} ${t('subscription_management.users_need_reset_date_fix')}`
      );
    } catch (error) {
      console.error('Scan failed:', error);
      setError(t('subscription_management.scan_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Fix all users with null reset dates
  const handleFixAllResetDates = async () => {
    setLoading(true);
    try {
      const response = await autoFixStripeIssues();

      // Check if response has the expected structure
      if (!response || !response.success) {
        console.error('Unexpected fix response structure:', response);
        setError('API returned unexpected fix response format. Please check backend implementation.');
        return;
      }

      // Get the number of fixed issues
      const fixedCount = response.issuesFixed || 0;

      setSuccess(
        `${t('subscription_management.fix_complete')}: ${fixedCount} ${t('subscription_management.users_fixed')}`
      );

      // Refresh the list
      await handleScanUsers();
    } catch (error) {
      console.error('Fix failed:', error);
      setError(t('subscription_management.fix_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle user selection from search
  const handleUserSelect = (user: UserWithSubscriptionDetails | null) => {
    if (user) {
      setSelectedUser({
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        nextUsageResetDate: user.nextUsageResetDate,
        customerId: user.customerId || null,
        subscriber: user.subscriber,
        paymentType: user.paymentType,
        subscriptionStartDate: user.subscriptionStartDate,
      });

      setNewResetDate(user.nextUsageResetDate ? new Date(user.nextUsageResetDate) : null);
    } else {
      setSelectedUser(null);
      setNewResetDate(null);
    }
    setIsEditingResetDate(false);
  };

  // Handle manual reset date update
  const handleUpdateResetDate = async () => {
    if (!selectedUser || !newResetDate) return;

    setLoading(true);
    try {
      const formattedDate = newResetDate.toISOString();
      await updateUserResetDate({
        userId: selectedUser.id,
        nextUsageResetDate: formattedDate,
      });

      // Update local state
      setSelectedUser({
        ...selectedUser,
        nextUsageResetDate: formattedDate,
      });

      setSuccess(t('subscription_management.reset_date_updated'));
      setIsEditingResetDate(false);
    } catch (error) {
      console.error('Update failed:', error);
      setError(t('subscription_management.reset_date_update_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Fix a single user
  const handleFixSingleUser = async (userId: number) => {
    setFixingUserId(userId);
    try {
      const response = await diagnoseSingleUser(userId, { fix: true });

      if (response.success) {
        setSuccess(`User ${userId} fixed successfully!`);

        // NEW: Use updatedUser data for efficient updates instead of full rescan
        if (response.updatedUser) {
          // Remove the fixed user from the issues list
          setIssues((prevIssues) => prevIssues.filter((issue) => issue.userId !== userId));

          // Add the fixed user to the working users list
          const fixedUser = {
            userId: response.updatedUser.userId,
            userEmail: response.updatedUser.userEmail,
            currentResetDate: response.updatedUser.nextUsageResetDate,
            isActive: true,
            customerId: response.updatedUser.customerId || '',
            paymentType: response.updatedUser.paymentType,
            subscriptionStartDate: response.updatedUser.subscriptionStartDate,
            issueType: 'none' as const,
          };

          setWorkingUsers((prevWorking) => [...prevWorking, fixedUser]);

          // Update stats
          setStats((prevStats) => ({
            ...prevStats,
            usersWithBrokenResetDate: prevStats.usersWithBrokenResetDate - 1,
            usersWithValidResetDate: prevStats.usersWithValidResetDate + 1,
          }));
        } else {
          // Fallback to full rescan if updatedUser is not provided
          await handleScanUsers();
        }
      } else {
        setError(`Fix failed for user ${userId}`);
      }
    } catch (error) {
      console.error('Single user fix failed:', error);
      setError(`Fix failed for user ${userId}`);
    } finally {
      setFixingUserId(null);
    }
  };

  // Helper function to extract subscription start date from various possible field names
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSubscriptionStartDate = (data: any): string | null => {
    if (!data) return null;
    // Try different field names that might contain the subscription start date
    return (
      data?.subscriptionStartDate ||
      data?.periodStarts ||
      data?.subscriptionStart ||
      data?.startDate ||
      data?.createdAt ||
      data?.created ||
      null
    );
  };

  const getIssueTypeLabel = (issueType: string) => {
    switch (issueType) {
      case 'missing_reset_date':
        return t('subscription_management.missing_reset_date');
      case 'outdated_reset_date':
        return t('subscription_management.outdated_reset_date');
      case 'misaligned_anniversary_date':
        return t('subscription_management.misaligned_anniversary_date');
      case 'invalid_reset_date':
        return t('subscription_management.invalid_reset_date');
      case 'none':
        return t('subscription_management.working_correctly');
      default:
        return t('subscription_management.unknown_issue');
    }
  };

  const getIssueTypeColor = (issueType: string) => {
    switch (issueType) {
      case 'missing_reset_date':
        return 'error'; // Red for missing dates
      case 'outdated_reset_date':
        return 'warning'; // Orange for expired dates
      case 'misaligned_anniversary_date':
        return 'info'; // Blue for misaligned dates
      case 'invalid_reset_date':
        return 'error'; // Red for invalid dates
      case 'none':
        return 'success'; // Green for working correctly
      default:
        return 'default';
    }
  };

  // SIMPLIFIED LOGIC: Since backend now ensures ALL users have proper reset dates
  // We only need to detect subscription type using paymentType
  const isAdminSubscription = (paymentType?: number) => {
    // Primary detection: paymentType 4 = Admin subscription
    return paymentType === 4;
  };

  const getSubscriptionTypeLabel = (paymentType?: number) => {
    if (isAdminSubscription(paymentType)) {
      return t('subscription_management.admin_subscription');
    }
    return t('subscription_management.recurring_subscription');
  };

  const getSubscriptionTypeColor = (paymentType?: number) => {
    if (isAdminSubscription(paymentType)) {
      return 'info'; // Admin subscriptions: blue
    }
    return 'success'; // Recurring subscriptions: green
  };

  const getUsageLimitLabel = (paymentType?: number) => {
    if (isAdminSubscription(paymentType)) {
      return t('subscription_management.unlimited_access');
    }
    return t('subscription_management.monthly_limit'); // "20 hours/month"
  };

  return (
    <Layout title={t('subscription_management.title')}>
      <Box p={3}>
        {/* Manual User Management Section */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {t('subscription_management.manual_user_management')}
                </Typography>

                {/* User Search */}
                <Box mb={3}>
                  <Autocomplete
                    options={userSearchOptions}
                    getOptionLabel={(option) => `${option.firstname} ${option.lastname} (${option.email})`}
                    loading={userSearchLoading}
                    onChange={(_, value) => handleUserSelect(value)}
                    onInputChange={(_, value) => searchUsers(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('subscription_management.search_user')}
                        placeholder={t('subscription_management.search_user_placeholder')}
                        variant="outlined"
                        fullWidth
                        helperText={t('subscription_management.search_min_chars')}
                      />
                    )}
                    filterOptions={(options) => options} // No client-side filtering, server handles it
                    noOptionsText={
                      userSearchLoading
                        ? t('subscription_management.searching')
                        : t('subscription_management.no_users_found')
                    }
                  />
                </Box>

                {/* Selected User Details */}
                {selectedUser && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('subscription_management.selected_user_details')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">
                          <strong>{t('subscription_management.name')}:</strong> {selectedUser.firstname}{' '}
                          {selectedUser.lastname}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('subscription_management.email')}:</strong> {selectedUser.email}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('subscription_management.stripe_customer_id')}:</strong>{' '}
                          {selectedUser.customerId || 'N/A'}
                        </Typography>
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Typography variant="body2" component="span">
                            <strong>{t('subscription_management.subscriber_status')}:</strong>
                          </Typography>
                          <Chip
                            label={
                              selectedUser.subscriber
                                ? t('subscription_management.active')
                                : t('subscription_management.inactive')
                            }
                            color={selectedUser.subscriber ? 'success' : 'default'}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Typography variant="body2" component="span">
                            <strong>{t('subscription_management.subscription_type')}:</strong>
                          </Typography>
                          <Chip
                            label={getSubscriptionTypeLabel(selectedUser.paymentType)}
                            color={getSubscriptionTypeColor(selectedUser.paymentType)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Typography variant="body2" component="span">
                            <strong>{t('subscription_management.usage_limit')}:</strong>
                          </Typography>
                          <Chip
                            label={getUsageLimitLabel(selectedUser.paymentType)}
                            color={isAdminSubscription(selectedUser.paymentType) ? 'info' : 'default'}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Typography variant="body2" component="span">
                            <strong>{t('subscription_management.anniversary_date')}:</strong>
                          </Typography>
                          <Chip
                            label={
                              selectedUser.subscriptionStartDate
                                ? new Date(selectedUser.subscriptionStartDate).toLocaleDateString('fi-FI')
                                : t('subscription_management.null_value')
                            }
                            color={selectedUser.subscriptionStartDate ? 'primary' : 'default'}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>{t('subscription_management.current_reset_date')}:</strong>
                        </Typography>
                        {!isEditingResetDate ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={
                                selectedUser.nextUsageResetDate
                                  ? new Date(selectedUser.nextUsageResetDate).toLocaleDateString('fi-FI')
                                  : t('subscription_management.null_value')
                              }
                              color={selectedUser.nextUsageResetDate ? 'success' : 'error'}
                              size="small"
                              icon={selectedUser.nextUsageResetDate ? <CheckIcon /> : <ErrorIcon />}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setIsEditingResetDate(true)}
                              startIcon={<EditIcon />}
                            >
                              {t('subscription_management.edit')}
                            </Button>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={1}>
                            <DatePicker
                              label={t('subscription_management.new_reset_date')}
                              value={newResetDate}
                              onChange={(date) => setNewResetDate(date)}
                              renderInput={(params) => <TextField {...params} size="small" />}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleUpdateResetDate}
                              disabled={!newResetDate}
                            >
                              {t('subscription_management.save')}
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => setIsEditingResetDate(false)}>
                              {t('subscription_management.cancel')}
                            </Button>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Bulk Operations Section */}
        <Typography variant="h5" gutterBottom>
          {t('subscription_management.bulk_operations')}
        </Typography>
        <Grid container spacing={3}>
          {/* Statistics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('subscription_management.active_subscribers')}
                </Typography>
                <Typography variant="h3" color="primary">
                  {stats.totalActiveSubscribers}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('subscription_management.users_with_active_stripe')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('subscription_management.broken_reset_dates')}
                </Typography>
                <Typography variant="h3" color="error">
                  {stats.usersWithBrokenResetDate}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('subscription_management.null_reset_date_users')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('subscription_management.working_correctly')}
                </Typography>
                <Typography variant="h3" color="success.main">
                  {stats.usersWithValidResetDate}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('subscription_management.valid_reset_date_users')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">{t('subscription_management.affected_users')}</Typography>
                  <Box display="flex" gap={2}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleScanUsers} disabled={loading}>
                      {loading ? t('subscription_management.scanning') : t('subscription_management.scan_users')}
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<BuildIcon />}
                      onClick={handleFixAllResetDates}
                      disabled={loading || issues.length === 0}
                    >
                      {t('subscription_management.fix_all_reset_dates')} ({issues.length})
                    </Button>
                  </Box>
                </Box>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {issues.length > 0 && (
                  <TableContainer>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>{t('subscription_management.user_email')}</TableCell>
                          <TableCell sx={{ minWidth: 150 }}>{t('subscription_management.anniversary_date')}</TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            {t('subscription_management.current_reset_date')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>{t('subscription_management.issue_type')}</TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            {t('subscription_management.stripe_customer_id')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>{t('subscription_management.subscription_type')}</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>{t('subscription_management.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {issues.map((issue, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{issue.userEmail}</TableCell>
                            <TableCell>
                              {issue.subscriptionStartDate ? (
                                <Chip
                                  label={new Date(issue.subscriptionStartDate).toLocaleDateString('fi-FI')}
                                  color="primary"
                                  size="small"
                                  icon={<CheckIcon />}
                                />
                              ) : (
                                <Chip
                                  label={t('subscription_management.null_value')}
                                  color="default"
                                  size="small"
                                  icon={<ErrorIcon />}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {issue.currentResetDate ? (
                                <Chip
                                  label={new Date(issue.currentResetDate).toLocaleDateString('fi-FI')}
                                  color="warning"
                                  size="small"
                                  icon={<BuildIcon />}
                                />
                              ) : (
                                <Chip
                                  label={t('subscription_management.null_value')}
                                  color="error"
                                  size="small"
                                  icon={<ErrorIcon />}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getIssueTypeLabel(issue.issueType)}
                                color={getIssueTypeColor(issue.issueType)}
                                size="small"
                                icon={<ErrorIcon />}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{issue.customerId}</TableCell>
                            <TableCell>
                              <Chip
                                label={getSubscriptionTypeLabel(issue.paymentType)}
                                color={getSubscriptionTypeColor(issue.paymentType)}
                                size="small"
                                icon={<CheckIcon />}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                onClick={() => handleFixSingleUser(issue.userId)}
                                disabled={fixingUserId === issue.userId}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                {fixingUserId === issue.userId
                                  ? t('subscription_management.fixing')
                                  : t('subscription_management.fix_user')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {issues.length === 0 && !loading && hasScanned && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">{t('subscription_management.all_reset_dates_working')}</Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Working Users Section */}
          {workingUsers.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    {t('subscription_management.working_users')} ({workingUsers.length})
                  </Typography>

                  <TableContainer>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 200 }}>{t('subscription_management.user_email')}</TableCell>
                          <TableCell sx={{ minWidth: 150 }}>{t('subscription_management.anniversary_date')}</TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            {t('subscription_management.current_reset_date')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>
                            {t('subscription_management.stripe_customer_id')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 120 }}>{t('subscription_management.subscription_type')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {workingUsers.map((user, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{user.userEmail}</TableCell>
                            <TableCell>
                              {user.subscriptionStartDate ? (
                                <Chip
                                  label={new Date(user.subscriptionStartDate).toLocaleDateString('fi-FI')}
                                  color="primary"
                                  size="small"
                                  icon={<CheckIcon />}
                                />
                              ) : (
                                <Chip
                                  label={t('subscription_management.null_value')}
                                  color="default"
                                  size="small"
                                  icon={<ErrorIcon />}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {user.currentResetDate ? (
                                <Chip
                                  label={new Date(user.currentResetDate).toLocaleDateString('fi-FI')}
                                  color="success"
                                  size="small"
                                  icon={<CheckIcon />}
                                />
                              ) : (
                                <Chip
                                  label={t('subscription_management.null_value')}
                                  color="error"
                                  size="small"
                                  icon={<ErrorIcon />}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{user.customerId}</TableCell>
                            <TableCell>
                              <Chip
                                label={getSubscriptionTypeLabel(user.paymentType)}
                                color={getSubscriptionTypeColor(user.paymentType)}
                                size="small"
                                icon={<CheckIcon />}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Layout>
  );
});

export default SubscriptionManagementScreen;
